import { useEffect, useState, useRef, ReactElement } from "react";
import { useSelector } from "react-redux";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Loader } from "@remo-co/ui-core/src/components/Loader";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { getUnixTimestamp } from "helpers/time/format";
import { makeSelectShuffleStartTime } from "modules/shuffleAttendees/redux/selectors";
import { COUNTDOWN_LENGTH } from "modules/shuffleAttendees/constants";
import { Actions, trackAction } from "modules/monitoring";
import { styles } from "./styles";

interface Props {
  size: number;
  isDialog?: boolean;
  onCountdownEnd: () => void;
}

export const ShuffleCountdownTimer = ({
  size,
  isDialog = false,
  onCountdownEnd,
}: Props): ReactElement => {
  const shuffleStartTime = useSelector(makeSelectShuffleStartTime());
  const [countdown, setCountdown] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    trackAction(Actions.SHUFFLE_COUNTDOWN_STARTED, {
      startTime: shuffleStartTime,
    });
  }, [shuffleStartTime]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (shuffleStartTime) {
      setCountdown(shuffleStartTime + COUNTDOWN_LENGTH - getUnixTimestamp());
      timer.current = setInterval(() => {
        const seconds =
          shuffleStartTime + COUNTDOWN_LENGTH - getUnixTimestamp();

        if (seconds < 0) {
          if (timer.current) {
            clearInterval(timer.current);
          }
          trackAction(Actions.SHUFFLE_COUNTDOWN_ENDED, {
            startTime: shuffleStartTime,
            endTime: getUnixTimestamp(),
          });
          onCountdownEnd();
        } else {
          setCountdown(seconds);
        }
      }, 1000);

      return () => {
        if (timer.current) {
          clearInterval(timer.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shuffleStartTime]);

  return (
    <Box sx={styles.countdownContainer}>
      <Box position="relative" display="inline-flex">
        <Loader size={size} thickness={3} />
        <Box
          top={0}
          left={0}
          bottom={0}
          right={0}
          position="absolute"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography
            variant={isDialog ? "h1" : "h3"}
            color="textPrimary"
            component="div"
          >
            {countdown}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
